<script lang="ts" setup>
import { useSerialize } from "@lxc/app-device-common";
import type { Rules } from "async-validator";
import { HttpStatusCode } from "axios";
import dayjs from "dayjs";
import { storeToRefs } from "pinia";
import { firmwareTypeOptions } from "~/constants/deviceFilters.config";
import { SearchMode } from "~/core/composables/useSearch";
import LxcError from "~/core/models/LxcError.class";
import type { OperationModel } from "~/core/models/OperationModel.interface";
import { OperationModelType } from "~/core/models/OperationModelType.enum";
import operationManagerService from "~/core/services/operationManager.service";
import filterUtils from "~/core/utils/filters.utils";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinCreateOperation } from "~/modules/dtwin/models/DtwinCreateOperation.interface";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DtwinOperation } from "~/modules/dtwin/models/DtwinOperation.interface";
import type { DtwinOperationFirmware } from "~/modules/dtwin/models/DtwinOperationFirmware.interface";
import type { DtwinOperationFirmwareInputParameters } from "~/modules/dtwin/models/DtwinOperationFirmwareInputParameters.interface";
import type { DtwinUpdateOperation } from "~/modules/dtwin/models/DtwinUpdateOperation.interface";
import { OperationCreationMode } from "~/modules/dtwin/models/OperationCreationMode.enum";
import { OperationScheduler } from "~/modules/dtwin/models/OperationScheduler.enum";
import { OperationServiceOrigin } from "~/modules/dtwin/models/OperationServiceOrigin.enum";
import { useGetFirmware } from "~/modules/firmware/composables/useGetFirmware";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import { FirmwareColumns } from "~/modules/firmware/models/FirmwareColumns.enum";
import { FirmwareStatus } from "~/modules/firmware/models/FirmwareStatus.enum";
import { useOrganizationStore } from "~/modules/organization/stores/useOrganizationStore";
import type { DtwinOperationForm, FilterOptions } from "~/types";
import { Filters } from "~/types";
import { getCustomInputParameters } from "~/utils/getCustomInputParameters";
import ILxcLightRotateCw from "~icons/lxc-light/rotate-cw";
import ILxcInfo from "~icons/lxc/info";

const props = defineProps<{
  organizationCode: number;
  dtwin: Dtwin;
  dtwinModel: DtwinModel;
  dtwinOperationModel: OperationModel;
  operation?: DtwinOperation<DtwinOperationFirmwareInputParameters>;
  show: boolean;
}>();

const emit = defineEmits(["update:show", "change"]);

const { t } = useI18n();
const serialize = useSerialize();
const { currentOrganization } = storeToRefs(useOrganizationStore());

const {
  isLoading: isLoadingFirmware,
  error: errorGetFirmware,
  getFirmware,
  result: resultGetFirmware,
} = useGetFirmware({ organizationCode: props.organizationCode });

const operationFormRef = ref();
const isSubmitting: Ref<boolean> = ref(false);
const minDate: Ref<Date> = ref(new Date());

const defaultOperationForm: DtwinOperationForm = {
  firmwareUid: undefined,
  deviceTwinUid: props.dtwin?.uid,
  launchPlannedAt: undefined,
  startsAtCreation: false,
};
const isLoadingOperation: Ref<boolean> = ref(false);
const isLoadingCheckOperation: Ref<boolean> = ref(false);
const isScheduledOrRunningOperations: Ref<boolean> = ref(false);
const errorLoadingOperation: Ref<LxcError | null | undefined> = ref();
const errorCheckOperation: Ref<LxcError | undefined | null> = ref();
const initCertificateFormStringified: Ref<string> = ref(
  serialize(defaultOperationForm),
);
const hasInternalScheduler: ComputedRef<boolean> = computed(
  () => props.dtwinOperationModel?.hasInternalScheduler ?? false,
);
const selectedFirmware: Ref<Firmware | undefined> = ref();
const form = reactive<DtwinOperationForm>({
  ...defaultOperationForm,
});
const initOperationForm: Ref<DtwinOperationForm> = ref({
  ...defaultOperationForm,
});
const edited: ComputedRef<boolean> = computed(() => {
  const stringifiedForm = serialize(form);
  return stringifiedForm !== initCertificateFormStringified.value;
});
const isEditionMode: ComputedRef<boolean> = computed(
  () => !!props.operation?.uid,
);

const isValidateDisabled: ComputedRef<boolean> = computed(
  () =>
    isLoadingFirmware.value ||
    isSubmitting.value ||
    !edited.value ||
    !selectedFirmware.value ||
    !props.dtwinOperationModel ||
    !operationFormRef.value.isValid,
);

// Certificate rules
const rules = computed(() => {
  const rulesConfig: Rules = {
    launchPlannedAt: [
      {
        required: true,
        message: t(
          "operation.firmware.update.dtwin.validation.launchPlannedAt",
        ),
        type: "date",
      },
    ],
    firmwareUid: [
      {
        required: true,
        message: t("operation.firmware.update.uuidRequired"),
        type: "string",
      },
    ],
  };

  return rulesConfig;
});

const statusOptions: FilterOptions = {
  label: t("filters.status"),
  options: Object.values(FirmwareStatus).map((value) => {
    return {
      value,
      label: `firmware.status.${value}`,
      disabled: true,
    };
  }),
};

const rangeOptions: Ref<FilterOptions | undefined> = ref(
  filterUtils.buildFilterOptions(firmwareTypeOptions, true),
);

/**
 * Firmware
 */
async function getFirmwareByUuid() {
  if (form.firmwareUid) {
    await getFirmware(form.firmwareUid);

    if (errorGetFirmware.value) {
      selectedFirmware.value = undefined;
    } else {
      selectedFirmware.value = resultGetFirmware.value;
    }
  } else {
    selectedFirmware.value = undefined;
  }
}
/**
 * End firmware
 */

async function setOperationDetailForm(
  dtwinOperation?: DtwinOperationFirmware | null,
) {
  Object.assign(
    form,
    dtwinOperation
      ? {
          firmwareUid: dtwinOperation.customInputParameters?.firmwareUid,
          deviceTwinUid: props.dtwin?.uid,
          launchPlannedAt: new Date(dtwinOperation.launchPlannedAt),
          startsAtCreation: dtwinOperation.startsAtCreation,
        }
      : { ...defaultOperationForm },
  );
  initOperationForm.value = { ...form };
  initCertificateFormStringified.value = serialize(form);
  await getFirmwareByUuid();
  // set timeout to be sure that the clearValidate is processed after updating the form inputs
  setTimeout(operationFormRef.value?.clearValidate, 0);
}

const onClose = () => {
  selectedFirmware.value = undefined;
  emit("update:show", false);
};

async function validate(): Promise<boolean> {
  return await operationFormRef.value?.validate().catch(() => false);
}

async function getOperation(): Promise<void> {
  isLoadingOperation.value = true;
  if (props.operation?.uid && currentOrganization.value?.code) {
    const response = await operationManagerService.getOperation(
      currentOrganization.value?.code,
      props.operation?.uid,
    );

    if (LxcError.check(response)) {
      errorLoadingOperation.value = response;
    } else {
      errorLoadingOperation.value = null;
      await setOperationDetailForm(
        response as unknown as DtwinOperationFirmware,
      );
    }
  } else {
    await setOperationDetailForm();
  }

  isLoadingOperation.value = false;
}

async function checkOperation(): Promise<void> {
  isLoadingCheckOperation.value = true;

  if (props.dtwin?.uid && !props.operation?.uid && currentOrganization?.value) {
    let filter: string | undefined;
    if (props.operation?.uid) {
      filter = `uid!=${props.operation?.uid}`;
    }
    const response = await operationManagerService.checkOperation({
      type: OperationModelType.FIRM_UPDATE,
      deviceTwinUid: [props.dtwin?.uid],
      filter,
      organizationCode: currentOrganization?.value.code,
    });

    if (LxcError.check(response)) {
      errorCheckOperation.value = response;
    } else {
      errorCheckOperation.value = null;
      isScheduledOrRunningOperations.value =
        response[props.dtwin.uid]?.FIRM_UPDATE ?? false;
    }
  } else {
    isScheduledOrRunningOperations.value = false;
  }

  isLoadingCheckOperation.value = false;
}

async function onConfirmCreateUpdateFirmware() {
  if (
    props.dtwin &&
    props.dtwinOperationModel &&
    form.launchPlannedAt &&
    currentOrganization.value?.code
  ) {
    isSubmitting.value = true;

    const customInputParameters = await getCustomInputParameters({
      operationModel: props.dtwinOperationModel,
      organizationCode: currentOrganization.value.code,
      firmware: selectedFirmware.value,
    });

    if (LxcError.check(customInputParameters)) {
      customInputParameters.notify(NotificationKey.error);
    } else {
      const createOperation: DtwinCreateOperation = {
        customInputParameters,
        deviceTwinUid: props.dtwin.uid,
        modelUid: props.dtwinOperationModel.uid,
        serviceOrigin: OperationServiceOrigin.UNITARY,
        creationMode: OperationCreationMode.MANUAL,
        launchPlannedAt: dayjs(form.launchPlannedAt).utc().toISOString(),
        expiredAt: dayjs(form.launchPlannedAt)
          .add(1, "month")
          .utc()
          .toISOString(),
        scheduler: OperationScheduler.SERVICE,
        startsAtCreation: form.startsAtCreation,
      };

      const operationResponse = await operationManagerService.createOperation(
        currentOrganization.value.code,
        createOperation,
      );

      if (LxcError.check(operationResponse)) {
        (operationResponse as LxcError).notify(NotificationKey.error);
      } else {
        showNotificationSuccess(t(NotificationKey.success));
        emit("change");
        onClose();
      }
    }

    isSubmitting.value = false;
  }
}

async function onConfirmModifyUpdateFirmware() {
  if (
    props.dtwin &&
    props.operation?.uid &&
    props.dtwinOperationModel &&
    form.launchPlannedAt &&
    currentOrganization.value?.code
  ) {
    isSubmitting.value = true;

    const customInputParameters = await getCustomInputParameters({
      organizationCode: currentOrganization.value.code,
      operationModel: props.dtwinOperationModel,
      firmware: selectedFirmware.value,
    });

    if (LxcError.check(customInputParameters)) {
      customInputParameters.notify(NotificationKey.error);
    } else {
      const updateOperation: DtwinUpdateOperation = {
        customInputParameters,
        launchPlannedAt: dayjs(form.launchPlannedAt).utc().toISOString(),
        expiredAt: dayjs(form.launchPlannedAt)
          .add(1, "month")
          .utc()
          .toISOString(),
        startsAtCreation: form.startsAtCreation,
      };

      const operationResponse = await operationManagerService.updateOperation(
        currentOrganization.value.code,
        props.operation?.uid,
        updateOperation,
      );

      if (LxcError.check(operationResponse)) {
        operationResponse.notify(NotificationKey.error);
      } else {
        showNotificationSuccess(t(NotificationKey.success));
        emit("change");
        onClose();
      }
    }

    isSubmitting.value = false;
  }
}

const onSubmit = async (): Promise<boolean> => {
  const dataValid = await validate();

  if (dataValid) {
    if (isEditionMode.value) {
      await onConfirmModifyUpdateFirmware();
    } else {
      await onConfirmCreateUpdateFirmware();
    }
  }

  return dataValid;
};

watch(
  () => props.show,
  async (isShown) => {
    if (isShown) {
      minDate.value = new Date();

      await Promise.all([getOperation(), checkOperation()]);
    }
  },
);

watch(
  () => selectedFirmware.value,
  (firmware?: Firmware | null) => {
    form.firmwareUid = firmware?.uuid;
  },
);
</script>

<template>
  <lxc-common-modal
    :show="show"
    class="!min-w-[40%] !max-w-4xl"
    body-class="!max-w-4xl"
    @close="onClose"
  >
    <template #header>
      <h3>{{ t("operation.firmware.update.label") }}</h3>
    </template>

    <template #body>
      <container-component
        :is-loading="isLoadingOperation || isLoadingFirmware"
        :error="
          errorLoadingOperation ||
          (errorGetFirmware &&
            errorGetFirmware.status !== HttpStatusCode.NotFound)
            ? errorGetFirmware
            : undefined
        "
      >
        <lxc-alert type="warning">
          <span class="break-normal">
            {{ t("operation.firmware.update.warningMessage") }}
          </span>
        </lxc-alert>

        <lxc-alert
          v-if="
            isScheduledOrRunningOperations &&
            !isLoadingCheckOperation &&
            !errorCheckOperation
          "
          type="warning"
        >
          <span class="break-normal">
            {{ t("operation.firmware.update.scheduled.warningMessage") }}
          </span>
        </lxc-alert>

        <p class="text-base">
          {{ t("operation.firmware.update.dtwin.description") }}
        </p>

        <lxc-form
          ref="operationFormRef"
          :rules="rules"
          :model="form"
          @submit.prevent="onSubmit"
        >
          <lxc-form-item
            :label="t('operation.firmware.update.dtwin.launchPlannedAt')"
            prop="launchPlannedAt"
          >
            <lxc-input
              v-model="form.launchPlannedAt"
              type="datetime"
              class="!pl-12"
              :date-format="t('dateFormat.date')"
              :min-date="minDate"
            />
          </lxc-form-item>

          <lxc-form-item
            v-if="hasInternalScheduler"
            :label="t('operation.firmware.update.dtwin.startsAtCreation')"
            prop="startsAtCreation"
          >
            <lxc-toggle
              v-model="form.launchPlannedAt"
              name="dtwinUpdateFirmwareModalLaunchPlannedAt"
              :value="true"
            />
          </lxc-form-item>

          <lxc-form-item prop="firmwareUid">
            <lxc-label>{{
              t("operation.firmware.update.firmwareLabel")
            }}</lxc-label>
            <lxc-information class="!mt-0 !mb-4 !pl-0">
              <template #component>
                <lxc-info-sign>
                  <i-lxc-info
                    width="1.25rem"
                    height="1.25rem"
                    view-box="0 0 1.25rem 1.25rem"
                  />
                </lxc-info-sign>
              </template>
              <template #body>
                {{ t("operation.firmware.update.dtwin.firmware") }}
              </template>
            </lxc-information>

            <firmware-list
              v-model:selected-firmware="selectedFirmware"
              :organization-code="organizationCode"
              no-action
              selectable
              :columns="[
                FirmwareColumns.NAME,
                FirmwareColumns.VERSION,
                FirmwareColumns.RANGE,
                FirmwareColumns.DECLINATION,
              ]"
              :default-filters="
                new Map<Filters, any>([
                  [Filters.STATUS, FirmwareStatus.ACTIVATED],
                  [Filters.RANGE, [dtwinModel.name]],
                  [
                    Filters.MODEL_DECLINATION,
                    dtwin.attributes?.range
                      ? [dtwin.attributes?.range]
                      : undefined,
                  ],
                  [
                    Filters.FIRMWARE_VERSIONS,
                    dtwin?.features.firmware?.reported?.firmwareVersion
                      ? `${dtwin.features.firmware.reported.firmwareVersion}`
                      : '',
                  ],
                  [
                    Filters.HARDWARE_VERSIONS,
                    dtwin?.attributes?.hardwareVersion
                      ? `${dtwin.attributes.hardwareVersion}`
                      : '',
                  ],
                ])
              "
              :range-options="rangeOptions"
              :search-mode="SearchMode.FILTER_SEARCH"
              :status-options="statusOptions"
            />
          </lxc-form-item>
        </lxc-form>
      </container-component>
    </template>

    <template #footer>
      <div class="flex grow shrink-0 justify-end gap-4">
        <lxc-button
          type="secondary"
          html-type="button"
          :title="t('button.cancel')"
          @click="onClose"
        >
          {{ t("button.cancel") }}
        </lxc-button>

        <lxc-button
          type="primary"
          html-type="button"
          :disabled="isValidateDisabled"
          :icon="isSubmitting ? ILxcLightRotateCw : undefined"
          :title="t('button.confirm')"
          @click="onSubmit"
        >
          {{ t("button.confirm") }}
        </lxc-button>
      </div>
    </template>
  </lxc-common-modal>
</template>
